import { useEffect, useMemo, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { ArrowLeft, Loader2, RefreshCw, Workflow } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { cn } from '@/utils/cn'
import { useTemplateSummaries } from '@/hooks/useCltTemplates'
import {
  useCalendarFreshness,
  useClearEventCltOverride,
  useRecomputeSbaCalendar,
  useSbaCalendarEventDetail,
  useSbaCalendarEvents,
  useSetEventCltOverride,
} from '@/hooks/useSbaCalendars'
import { EventDatesTable } from '@/components/CltCalendars/EventDatesTable'
import {
  EventFiltersBar,
  type EventFilters,
} from '@/components/CltCalendars/EventFiltersBar'
import { SeasonalMilestonesTable } from '@/components/CltCalendars/SeasonalMilestonesTable'
import type { UiCalendarLogicTemplateSummary } from '@/api/clt.adapter'
import type { CltOverride, ComputeStatus } from '@/api/clt.types'

type AppliedSource = 'override' | 'calendar' | 'default'

const SOURCE_LABEL: Record<AppliedSource, string> = {
  override: 'Overridden on this event',
  calendar: 'Applied to the calendar',
  default: 'Default template',
}

const STATUS_VARIANT: Record<
  string,
  'default' | 'secondary' | 'outline' | 'destructive'
> = {
  ready: 'default',
  computing: 'secondary',
  pending: 'outline',
  error: 'destructive',
  failed: 'destructive',
}

function formatStatus(status: ComputeStatus | string | null) {
  if (!status) return 'Unknown'
  return status.charAt(0).toUpperCase() + status.slice(1)
}

function resolveAppliedSummary(
  summaries: UiCalendarLogicTemplateSummary[],
  cltOverride: CltOverride | null | undefined,
  calendarDefaultTemplateId: string | null | undefined
): { summary: UiCalendarLogicTemplateSummary | null; source: AppliedSource } {
  const overrideId = cltOverride?.template_id
  if (overrideId) {
    const s = summaries.find(x => x.id === overrideId)
    if (s) return { summary: s, source: 'override' }
  }
  if (calendarDefaultTemplateId) {
    const s = summaries.find(x => x.id === calendarDefaultTemplateId)
    if (s) return { summary: s, source: 'calendar' }
  }
  const def = summaries.find(s => s.isDefault) ?? null
  return { summary: def, source: 'default' }
}

export default function AdminCltCalendarDetail() {
  const { calendarId } = useParams<{ calendarId: string }>()
  const navigate = useNavigate()

  const freshness = useCalendarFreshness(calendarId)
  const { calendar, isLoading: calLoading } = freshness
  const { data: events = [], isLoading: evLoading } =
    useSbaCalendarEvents(calendarId)
  const { data: summaries = [] } = useTemplateSummaries()

  const [selectedEventId, setSelectedEventId] = useState('')
  const selectedEvent =
    events.find(e => e.id === selectedEventId) ?? events[0] ?? null
  const { data: selectedEventDetail } = useSbaCalendarEventDetail(
    calendarId,
    selectedEvent?.id
  )

  const setOverrideMutation = useSetEventCltOverride(calendarId ?? '')
  const clearOverrideMutation = useClearEventCltOverride(calendarId ?? '')
  const recomputeMutation = useRecomputeSbaCalendar(calendarId)

  const [eventSearch, setEventSearch] = useState('')
  const [filters, setFilters] = useState<EventFilters>({})

  const filteredEvents = useMemo(() => {
    const q = eventSearch.trim().toLowerCase()
    return q ? events.filter(e => e.name.toLowerCase().includes(q)) : events
  }, [events, eventSearch])

  const isSba = calendar?.is_sba ?? true

  const applied = selectedEvent
    ? resolveAppliedSummary(
        summaries,
        selectedEventDetail?.clt_override,
        calendar?.default_logic_template_id
      )
    : null

  const appliedTemplateId = applied?.summary?.id ?? ''
  const [stagedTemplateId, setStagedTemplateId] = useState('')
  useEffect(() => {
    setStagedTemplateId(appliedTemplateId)
  }, [selectedEvent?.id, appliedTemplateId])

  const liveStatus = freshness.status

  if (calLoading) {
    return (
      <div className="flex flex-col items-center justify-center gap-2 py-24 text-sm text-muted-foreground">
        <Loader2 className="h-5 w-5 animate-spin" />
        Loading calendar…
      </div>
    )
  }

  if (!calendar) {
    return (
      <div className="flex flex-col items-center justify-center gap-4 py-24">
        <p className="text-sm text-muted-foreground">Calendar not found.</p>
        <Button
          variant="outline"
          onClick={() => navigate('/admin/clt-calendars')}
          className="cursor-pointer"
        >
          Back to Calendars
        </Button>
      </div>
    )
  }

  const isOverridden = Boolean(selectedEventDetail?.clt_override?.template_id)

  return (
    <div className="flex flex-col gap-6 p-6">
      <div>
        <button
          onClick={() =>
            navigate(`/admin/clt-calendars?type=${isSba ? 'sba' : 'seasonal'}`)
          }
          className="mb-2 flex items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground cursor-pointer"
        >
          <ArrowLeft className="h-4 w-4" />
          Calendars
        </button>
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-semibold text-foreground">
            {calendar.name}
          </h1>
          {isSba && liveStatus && (
            <Badge
              variant={STATUS_VARIANT[liveStatus] ?? 'secondary'}
              className="text-[11px]"
            >
              {formatStatus(liveStatus)}
              {freshness.progress != null &&
                ` · ${Math.round(freshness.progress * 100)}%`}
            </Badge>
          )}
          {isSba && (
            <Button
              size="sm"
              variant="outline"
              className="ml-auto cursor-pointer"
              disabled={
                !calendarId ||
                recomputeMutation.isPending ||
                freshness.isComputing
              }
              onClick={() => recomputeMutation.mutate()}
            >
              <RefreshCw
                className={cn(
                  'h-3.5 w-3.5',
                  (recomputeMutation.isPending || freshness.isComputing) &&
                    'animate-spin'
                )}
              />
              {recomputeMutation.isPending ? 'Triggering…' : 'Recompute'}
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 md:grid-cols-[240px_1fr]">
        {/* Events list */}
        <div className="flex flex-col gap-1">
          <span className="px-1 pb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Events ({events.length})
          </span>
          <Input
            value={eventSearch}
            onChange={e => setEventSearch(e.target.value)}
            placeholder="Search events..."
            className="mb-1 h-8"
          />
          <div className="flex max-h-72 flex-col gap-1 overflow-y-auto pr-1 md:max-h-[60vh]">
            {evLoading ? (
              <p className="px-1 py-2 text-xs text-muted-foreground">
                Loading…
              </p>
            ) : filteredEvents.length === 0 ? (
              <p className="px-1 py-2 text-xs text-muted-foreground">
                No events match.
              </p>
            ) : (
              filteredEvents.map(ev => (
                <button
                  key={ev.id}
                  onClick={() => setSelectedEventId(ev.id)}
                  className={cn(
                    'flex items-center justify-between rounded-md border px-3 py-2 text-left text-sm transition-colors cursor-pointer',
                    ev.id === selectedEvent?.id
                      ? 'border-primary bg-primary/5 font-medium text-foreground'
                      : 'border-border text-foreground hover:bg-muted/50'
                  )}
                >
                  {ev.name}
                  {isSba && ev.clt_override_template_id && (
                    <Badge
                      variant="secondary"
                      className="px-1.5 py-0 text-[10px]"
                    >
                      override
                    </Badge>
                  )}
                </button>
              ))
            )}
          </div>
        </div>

        {/* Selected event panel */}
        {selectedEvent && (
          <div className="flex flex-col gap-4">
            {/* Applied logic — SBA only */}
            {isSba && applied && (
              <div className="rounded-lg border border-border bg-card p-4">
                <div className="max-w-[420px] space-y-1.5">
                  <label className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                    <Workflow className="h-3.5 w-3.5" />
                    Applied Calendar Logic Template
                  </label>
                  <div className="flex items-center gap-2">
                    <Select
                      value={stagedTemplateId}
                      onValueChange={setStagedTemplateId}
                    >
                      <SelectTrigger className="h-9 flex-1">
                        <SelectValue placeholder="Select a template" />
                      </SelectTrigger>
                      <SelectContent>
                        {summaries.map(t => (
                          <SelectItem key={t.id} value={t.id}>
                            {t.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button
                      size="sm"
                      disabled={
                        !stagedTemplateId ||
                        stagedTemplateId === appliedTemplateId ||
                        setOverrideMutation.isPending
                      }
                      onClick={() =>
                        setOverrideMutation.mutate({
                          eventId: selectedEvent.id,
                          templateId: stagedTemplateId,
                        })
                      }
                    >
                      {setOverrideMutation.isPending ? 'Applying…' : 'Apply'}
                    </Button>
                  </div>
                  <div className="flex items-center gap-2 pt-0.5">
                    <Badge variant="outline" className="text-[11px]">
                      {SOURCE_LABEL[applied.source]}
                    </Badge>
                    {isOverridden && (
                      <button
                        onClick={() =>
                          clearOverrideMutation.mutate(selectedEvent.id)
                        }
                        className="text-xs text-muted-foreground underline-offset-2 hover:underline cursor-pointer"
                      >
                        Reset to inherited
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Filters — SBA only */}
            {isSba && <EventFiltersBar value={filters} onChange={setFilters} />}

            {/* Computed dates — SBA only, gated on calendar readiness */}
            {isSba &&
              calendarId &&
              (freshness.isReady ? (
                <EventDatesTable
                  calendarId={calendarId}
                  eventId={selectedEvent.id}
                  filters={filters}
                />
              ) : freshness.isFailed ? (
                <div className="rounded-lg border border-dashed border-destructive/40 p-6 text-center text-sm text-destructive">
                  <p>{freshness.detail ?? 'Calendar compute failed.'}</p>
                  <p className="mt-1 text-muted-foreground">
                    Use the Recompute button above to try again.
                  </p>
                </div>
              ) : (
                <div className="flex items-center justify-center gap-2 rounded-lg border border-dashed border-border p-6 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Computing dates…
                  {freshness.progress != null &&
                    ` · ${Math.round(freshness.progress * 100)}%`}
                </div>
              ))}

            {/* Milestone dates — Seasonal only */}
            {!isSba && calendarId && (
              <SeasonalMilestonesTable
                calendarId={calendarId}
                eventId={selectedEvent.id}
              />
            )}
          </div>
        )}
      </div>
    </div>
  )
}
